import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import {
    CreateExpertPlanRequestSchema,
    ExpertPlanMutationResultSchema,
    ExpertPlanListResponseSchema,
    ExpertPlanSchema,
    ExpertSubscriptionSchema,
    ListExpertSubscriptionInvoicesItemSchema,
    RegisterExpertSubscriptionInvoiceRequestSchema,
    RegisterExpertSubscriptionInvoiceResponseSchema,
    UpdateExpertPlanRequestSchema,
    UpdateExpertSubscriptionInvoiceRequestSchema,
    ExpertSubscriptionInvoiceSchema,
} from "../schemas/expert-subscriptions";

export interface AdminExpertPlanListQueryParams extends QueryParams {
    is_active?: boolean;
}

export interface AdminExpertSubscriptionsListQueryParams extends QueryParams {
    invoice_limit?: number;
    management?: "all" | "managed" | "manual";
}

export interface AdminExpertSubscriptionInvoicesListQueryParams extends QueryParams {
    limit?: number;
    cursor?: string;
}

export function createAdminExpertSubscriptionsApi(http: HttpClient, baseUrl: string) {
    const adminPlansBaseUrl = `${baseUrl}/admin/expert-plans`;
    const adminExpertsBaseUrl = `${baseUrl}/admin/experts`;

    return {
        listPlans: createEndpoint<
            z.infer<typeof ExpertPlanListResponseSchema>,
            unknown,
            AdminExpertPlanListQueryParams
        >(http, "GET", "", {
            base: adminPlansBaseUrl,
            responseSchema: ExpertPlanListResponseSchema,
        }),

        getPlan: createEndpoint<
            z.infer<typeof ExpertPlanSchema>,
            unknown,
            QueryParams,
            { plan: number }
        >(http, "GET", "/:plan", {
            base: adminPlansBaseUrl,
            responseSchema: ExpertPlanSchema,
            params: ["plan"],
        }),

        createPlan: createEndpoint<
            z.infer<typeof ExpertPlanMutationResultSchema>,
            z.input<typeof CreateExpertPlanRequestSchema>
        >(http, "POST", "", {
            base: adminPlansBaseUrl,
            responseSchema: ExpertPlanMutationResultSchema,
        }),

        updatePlan: createEndpoint<
            z.infer<typeof ExpertPlanMutationResultSchema>,
            z.input<typeof UpdateExpertPlanRequestSchema>,
            QueryParams,
            { plan: number }
        >(http, "PUT", "/:plan", {
            base: adminPlansBaseUrl,
            responseSchema: ExpertPlanMutationResultSchema,
            params: ["plan"],
        }),

        deletePlan: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { plan: number }
        >(http, "DELETE", "/:plan", {
            base: adminPlansBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["plan"],
        }),

        listSubscriptions: createEndpoint<
            z.infer<typeof ExpertSubscriptionSchema>[],
            unknown,
            AdminExpertSubscriptionsListQueryParams,
            { expert: number }
        >(http, "GET", "/:expert/subscriptions", {
            base: adminExpertsBaseUrl,
            responseSchema: z.array(ExpertSubscriptionSchema),
            params: ["expert"],
        }),

        registerInvoice: createEndpoint<
            z.infer<typeof RegisterExpertSubscriptionInvoiceResponseSchema>,
            z.input<typeof RegisterExpertSubscriptionInvoiceRequestSchema>,
            QueryParams,
            { expert: number }
        >(http, "POST", "/:expert/subscriptions", {
            base: adminExpertsBaseUrl,
            responseSchema: RegisterExpertSubscriptionInvoiceResponseSchema,
            params: ["expert"],
        }),

        cancelSubscription: createEndpoint<
            z.infer<typeof ExpertSubscriptionSchema>,
            unknown,
            QueryParams,
            { expert: number; subscription: number }
        >(http, "POST", "/:expert/subscriptions/:subscription/cancel", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertSubscriptionSchema,
            params: ["expert", "subscription"],
        }),

        listInvoices: createEndpointWithPagination<
            z.infer<typeof ListExpertSubscriptionInvoicesItemSchema>,
            unknown,
            unknown,
            AdminExpertSubscriptionInvoicesListQueryParams,
            { expert: number; subscription: number }
        >(http, "GET", "/:expert/subscriptions/:subscription/invoices", {
            base: adminExpertsBaseUrl,
            itemSchema: ListExpertSubscriptionInvoicesItemSchema,
            params: ["expert", "subscription"],
        }),

        updateInvoice: createEndpoint<
            z.infer<typeof ExpertSubscriptionInvoiceSchema>,
            z.input<typeof UpdateExpertSubscriptionInvoiceRequestSchema>,
            QueryParams,
            { expert: number; subscription: number; invoice: number }
        >(http, "PUT", "/:expert/subscriptions/:subscription/invoices/:invoice", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertSubscriptionInvoiceSchema,
            params: ["expert", "subscription", "invoice"],
        }),

        deleteInvoice: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { expert: number; subscription: number; invoice: number }
        >(http, "DELETE", "/:expert/subscriptions/:subscription/invoices/:invoice", {
            base: adminExpertsBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["expert", "subscription", "invoice"],
        }),
    };
}

export type AdminExpertSubscriptionsApi = ReturnType<typeof createAdminExpertSubscriptionsApi>;
